import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { MoreVertical, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import AddAgencyModal from '../Modals/AddAgencyModal'
import DeletePopUp from '../Modals/DeletePopUp'
import ActivatePopUp from '../Modals/ActivatePopUp'
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAgencies, deleteAgency, deleteAgencyPermanent, activateAgency, setAgency } from '../../requests';
import { setUserInformation } from '../../_store/reducers/userReducer';
import { clearContext } from '../../_store/reducers/contextReducer';
import { notifyError, notifySuccess } from '../../utils/helper';
import { deleteAuthToken } from '../../utils/helper';
import { removeUserInformation } from '../../_store/reducers/userReducer';
import inviteStyles from '../../_css/Invite.module.css';
import { Card, CardTitle, TableHeader, TableCell, Badge } from '../UI';
import { toggleRefresh } from '../../_store/reducers/refreshReducer';




const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 5;

const Index = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddAgencyModal, setShowAddAgencyModal] = useState(false);
  const [showViewAgencyModal, setShowViewAgencyModal] = useState(false);
  const [viewAgencyData, setViewAgencyData] = useState(null);
  const [viewAgencyStats, setViewAgencyStats] = useState(null);
  const [viewAgencyAdmins, setViewAgencyAdmins] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyToDeactivate, setAgencyToDeactivate] = useState(null);
  const [agencyToActivate, setAgencyToActivate] = useState(null);
  const [agencyToDelete, setAgencyToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
    perPage: DEFAULT_LIMIT,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');

  const handleGetAgencies = async (pageNum = page, limitNum = limit, searchTerm = search) => {
    dispatch(startLoader());
    try {
      const response = await getAllAgencies({
        page: pageNum,
        limit: limitNum,
        ...(searchTerm?.trim() && { search: searchTerm.trim() }),
      });
      if (response?.status === 200) {
        const { data = [], pagination: paginationData = {} } = response?.data || {};
        setAgencies(data);
        setPagination({
          total: paginationData.total ?? 0,
          totalPages: paginationData.totalPages ?? 1,
          currentPage: paginationData.currentPage ?? 1,
          perPage: paginationData.perPage ?? limitNum,
          hasNextPage: paginationData.hasNextPage ?? false,
          hasPrevPage: paginationData.hasPrevPage ?? false,
        });
      }
    } catch (error) {
      //is status code 401, then redirect to login page
      if (error?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login');
        notifyError('Session expired. Please login again.'||error?.response?.data?.message);
      }
      else {
        console.log(error);
        notifyError(error?.response?.data?.message || 'Something went wrong');
      }
    } finally {
      dispatch(stopLoader());
    }
  };

  const refreshAgencyList = () => {
    handleGetAgencies(page, limit);
  };

  const getAgencyId = (item) => item?.agency?.id ?? item?.agency?._id ?? item?.id ?? item?._id;

  const handleAgencyRowClick = async (item) => {
    const agency = item?.agency ?? item;
    const id = getAgencyId(item);
    if (!id) return;

    //is status is inactive the return and show a popup to activate the agency
    if (!agency.isActive) {
      notifyError('Please activate the agency to view the brands');
      return;
    }
    dispatch(startLoader());
    try {
      const response = await setAgency(Number(id));
      if (response?.status < 400) {
        dispatch(
          setUserInformation(
            user.fullName,
            user.userId,
            user.email,
            user.status,
            user.isSuperAdmin,
            id,
            user.invitedById,
            agency?.name ?? agency?.agencyName ?? '',
            agency?.email ?? '',
            user.role
          )
        );
        dispatch(clearContext());

        dispatch(toggleRefresh());
        navigate(`/brands?agency_id=${id}`);
      } else {
        notifyError(response?.data?.message || 'Failed to select agency');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Something went wrong');
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleDeactivateAgency = async (id) => {
    dispatch(startLoader());
    try {
      const response = await deleteAgency(id);
      if (response?.status === 200) {
        notifySuccess('Agency deactivated successfully');
        setAgencyToDeactivate(null);
        setOpenDropdownId(null);
        await handleGetAgencies(page, limit);
        dispatch(toggleRefresh());
      } else {
        notifyError(response?.data?.message || 'Failed to deactivate agency');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Something went wrong');
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleActivateAgency = async (id) => {
    dispatch(startLoader());
    try {
      const response = await activateAgency(id);
      if (response?.status === 200) {
        notifySuccess('Agency activated successfully');
        setOpenDropdownId(null);
        await handleGetAgencies(page, limit);
        dispatch(toggleRefresh());
      } else {
        notifyError(response?.data?.message || 'Failed to activate agency');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Something went wrong');
    } finally {
      dispatch(stopLoader());
    }
  };



  const handleConfirmDeactivate = () => {
    if (agencyToDeactivate) {
      const id = getAgencyId(agencyToDeactivate);
      if (id) handleDeactivateAgency(id);
    }
  };

  const handleDeleteAgencyPermanent = async (id) => {
    dispatch(startLoader());
    try {
      const response = await deleteAgencyPermanent(id);
      if (response?.status === 200) {
        notifySuccess('Agency deleted');
        setAgencyToDelete(null);
        setOpenDropdownId(null);
        await handleGetAgencies(page, limit);
        dispatch(toggleRefresh());
      } else {
        notifyError(response?.data?.message || 'Failed to delete agency');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Something went wrong');
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleConfirmDelete = () => {
    if (agencyToDelete) {
      const id = getAgencyId(agencyToDelete);
      if (id) handleDeleteAgencyPermanent(id);
    }
  };

  const handleConfirmActivate = async () => {
    if (agencyToActivate) {
      const id = getAgencyId(agencyToActivate);
      if (id) await handleActivateAgency(id);
      setAgencyToActivate(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
      handleGetAgencies(newPage, limit);
    }
  };

  const handleViewAgency = (item) => {
    setViewAgencyData(item?.agency ?? item.agency);
    setViewAgencyStats(item?.stats ?? item.stats);
    setViewAgencyAdmins(item?.admins ?? item.admins);
    setShowViewAgencyModal(true);
  };

  const closeViewModal = () => {
    setShowViewAgencyModal(false);
    setViewAgencyData(null);
    refreshAgencyList();
  };

  const closeAddModal = () => {
    setShowAddAgencyModal(false);
    refreshAgencyList();
  };

  useEffect(() => {
    handleGetAgencies(DEFAULT_PAGE, DEFAULT_LIMIT);
  }, []);

  const isFirstSearchRun = React.useRef(true);
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(DEFAULT_PAGE);
      handleGetAgencies(DEFAULT_PAGE, limit, search);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const state = location?.state;
    if (state?.openViewAgencyModal && state?.agencyData) {
      setViewAgencyData(state.agencyData);
      setViewAgencyStats(state.agencyStats ?? null);
      setViewAgencyAdmins(state.admins ?? []);
      setShowViewAgencyModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state?.openViewAgencyModal, location?.pathname, location?.state, navigate]);

  if (showViewAgencyModal) {
    return <AddAgencyModal isView={true} onClose={closeViewModal} data={viewAgencyData} stats={viewAgencyStats} admins={viewAgencyAdmins} />;
  }

  if (showAddAgencyModal) {
    return <AddAgencyModal onClose={closeAddModal} />;
  }



  return (
    <div className={inviteStyles.page}>
      <div className={inviteStyles.headerRow}>
        <div className={inviteStyles.titleBlock}>
          <h1>Agencies</h1>
          <p>List all agencies and manage existing ones</p>
        </div>
        <button
          type="button"
          className={inviteStyles.primaryBtn}
          onClick={() => setShowAddAgencyModal(true)}
        >
          + New Agency
        </button>
      </div>

      <Card>
        <CardTitle>All agencies</CardTitle>
        <div className={inviteStyles.searchBarWrap}>
          <Search size={16} className={inviteStyles.searchIcon} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by agency name..."
            className={inviteStyles.searchInput}
          />
        </div>
        <div className={inviteStyles.tableWrap}>
          <table className={inviteStyles.table}>
            <thead>
              <TableHeader
                columns={['Agency ID', 'Agency Name', 'Simulations', 'Status', 'Actions']}
              />
            </thead>
            <tbody>
              {agencies.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 0, border: 'none' }}>
                    <p className={inviteStyles.emptyText}>No agencies found</p>
                  </td>
                </tr>
              ) : (
                agencies.map((item, index) => {
                  const agency = item?.agency ?? item;
                  const stats = item?.stats ?? {};
                  const rowId = agency?.id ?? agency?._id ?? index;
                  return (
                    <tr
                      key={rowId}
                      className={inviteStyles.clickableRow}
                      onClick={() => handleAgencyRowClick(item)}
                    >
                      <TableCell>
                        {agency?.agency_code ?? (agency?.id ? `MPSA-${agency.id}` : '—')}
                      </TableCell>
                      <TableCell bold>
                        {agency?.name ?? agency?.agencyName}
                      </TableCell>
                      <TableCell>
                        {stats?.totalActiveProjects ?? stats?.totalProjects ?? 0}
                      </TableCell>
                      <TableCell>
                        <Badge color={agency.isActive ? '#22c55e' : '#6b7280'}>
                          {agency.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className={inviteStyles.actionsCell}>
                          <div className={inviteStyles.dropdownWrap}>
                            <button
                              type="button"
                              className={inviteStyles.actionIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                if (openDropdownId === rowId) {
                                  setOpenDropdownId(null);
                                  setDropdownAnchor(null);
                                } else {
                                  setOpenDropdownId(rowId);
                                  setDropdownAnchor(rect);
                                }
                              }}
                              aria-haspopup="menu"
                              aria-expanded={openDropdownId === rowId}
                              aria-label="Actions"
                              title="Actions"
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openDropdownId === rowId &&
                              dropdownAnchor &&
                              ReactDOM.createPortal(
                                <>
                                  <div
                                    className={inviteStyles.dropdownBackdrop}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenDropdownId(null);
                                      setDropdownAnchor(null);
                                    }}
                                    aria-hidden="true"
                                  />
                                  <ul
                                    className={inviteStyles.dropdownMenu}
                                    role="menu"
                                    style={{
                                      top: dropdownAnchor.top + 4,
                                      right: typeof window !== 'undefined' ? window.innerWidth - dropdownAnchor.right : 0,
                                    }}
                                  >
                                    <li role="none">
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className={inviteStyles.dropdownItem}
                                        onClick={() => {
                                          handleViewAgency(item);
                                          setOpenDropdownId(null);
                                          setDropdownAnchor(null);
                                        }}
                                      >
                                        Details
                                      </button>
                                    </li>
                                    {!agency.isActive && (
                                      <li role="none">
                                        <button
                                          type="button"
                                          role="menuitem"
                                          className={inviteStyles.dropdownItem}
                                          onClick={() => {
                                            setAgencyToActivate(item);
                                            setOpenDropdownId(null);
                                            setDropdownAnchor(null);
                                          }}
                                        >
                                          Activate
                                        </button>
                                      </li>
                                    )}
                                    {agency.isActive && (
                                      <li role="none">
                                        <button
                                          type="button"
                                          role="menuitem"
                                          className={inviteStyles.dropdownItemDanger}
                                          onClick={() => {
                                            setAgencyToDeactivate(item);
                                            setOpenDropdownId(null);
                                            setDropdownAnchor(null);
                                          }}
                                        >
                                          Deactivate
                                        </button>
                                      </li>
                                    )}
                                    <li role="none">
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className={inviteStyles.dropdownItemDanger}
                                        onClick={() => {
                                          setAgencyToDelete(item);
                                          setOpenDropdownId(null);
                                          setDropdownAnchor(null);
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                </>,
                                document.body
                              )}
                          </div>
                        </div>
                      </TableCell>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className={inviteStyles.pagination}>
          <p className={inviteStyles.paginationInfo}>
            Showing {(pagination.currentPage - 1) * pagination.perPage + 1}–
            {pagination.total === 0 ? 0 : Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of{' '}
            {pagination.total}
            {pagination.totalPages > 1 && (
              <>
                {' '}
                · Page {pagination.currentPage} of {pagination.totalPages}
              </>
            )}
          </p>
          {pagination.totalPages > 1 && (
            <div className={inviteStyles.paginationBtns}>
              <button
                type="button"
                className={inviteStyles.pageBtn}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className={inviteStyles.pageBtn}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </Card>

      {agencyToDeactivate && (
        <DeletePopUp
          title="Deactivate agency"
          message={`Are you sure you want to deactivate "${agencyToDeactivate?.agency?.name ?? agencyToDeactivate?.name ?? agencyToDeactivate?.agencyName}"?`}
          onClose={() => setAgencyToDeactivate(null)}
          onDelete={handleConfirmDeactivate}
        />
      )}

      {agencyToActivate && (
        <ActivatePopUp
          title="Activate agency"
          message={`Are you sure you want to activate "${agencyToActivate?.agency?.name ?? agencyToActivate?.name ?? agencyToActivate?.agencyName}"?`}
          onClose={() => setAgencyToActivate(null)}
          onActivate={handleConfirmActivate}
        />
      )}

      {agencyToDelete && (
        <DeletePopUp
          variant="delete"
          title="Delete agency"
          message={`This will delete "${agencyToDelete?.agency?.name ?? agencyToDelete?.name ?? agencyToDelete?.agencyName}". Contracts will be kept but marked deleted. The open contract is closed without renewal, scheduled runs stop and its users can no longer log in. Name and history are kept in the archive (Clients & contracts → Deleted); the name can be used for a new agency, and this agency cannot be reactivated.`}
          onClose={() => setAgencyToDelete(null)}
          onDelete={handleConfirmDelete}
        />
      )}
    </div>
  )
}

export default Index